import { useState } from 'react';
import { Card, Button, Loading } from '../common';
import { useAuthStore } from '../../store/authStore';
import { readingsAPI } from '../../api/readings';

import type { TarotReading } from '../../types/tarot';

interface ExportOptions {
  includeProfile: boolean;
  includeReadings: boolean;
  includePrivateReadings: boolean;
  includeStats: boolean;
  format: 'json' | 'csv';
  dateRange: 'all' | 'last30' | 'last90' | 'lastyear';
}

export function DataExport() {
  const { user } = useAuthStore();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeProfile: true,
    includeReadings: true,
    includePrivateReadings: true,
    includeStats: true,
    format: 'json',
    dateRange: 'all'
  });

  const handleOptionChange = (option: keyof ExportOptions, value: boolean | string) => {
    setExportOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  const getDateRangeFilter = () => {
    const now = new Date();
    switch (exportOptions.dateRange) {
      case 'last30':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case 'last90':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      case 'lastyear':
        return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      default:
        return null;
    }
  };

  const exportToJSON = (data: any) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `tarot-data-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = (readings: TarotReading[]) => {
    const headers = [
      'ID',
      'Question',
      'Spread',
      'Cards',
      'Is Favorite',
      'Is Private',
      'Status',
      'Tags',
      'Created At',
      'Updated At'
    ];

    const csvContent = [
      headers.join(','),
      ...readings.map(reading => [
        reading.id,
        `"${reading.question.replace(/"/g, '""')}"`,
        reading.spreadId,
        `"${reading.cards.map(c => `${c.name}${c.isReversed ? ' (R)' : ''}`).join('; ')}"`,
        reading.isFavorite,
        reading.isPrivate,
        reading.status,
        `"${reading.tags.join('; ')}"`,
        reading.createdAt,
        reading.updatedAt
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `tarot-readings-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setError(null);
      setSuccess(null);

      const exportData: any = {
        exportDate: new Date().toISOString(),
        exportOptions: exportOptions
      };

      // Include profile data
      if (exportOptions.includeProfile && user) {
        exportData.profile = {
          userId: user.userId,
          email: user.email,
          displayName: user.displayName,
          nickname: user.nickname,
          membershipTier: user.membershipTier,
          registrationDate: user.registrationDate,
          preferences: user.preferences
        };
      }

      // Include readings data
      if (exportOptions.includeReadings) {
        const response = await readingsAPI.getReadings();
        let readings = response.data.readings || [];

        // Filter by date range
        const dateFilter = getDateRangeFilter();
        if (dateFilter) {
          readings = readings.filter((reading: TarotReading) => 
            new Date(reading.createdAt) >= dateFilter
          );
        }

        // Filter private readings if not included
        if (!exportOptions.includePrivateReadings) {
          readings = readings.filter((reading: TarotReading) => !reading.isPrivate);
        }

        exportData.readings = readings;
        exportData.readingsCount = readings.length;
      }

      // Include statistics
      if (exportOptions.includeStats) {
        try {
          const statsResponse = await readingsAPI.getStats();
          exportData.statistics = statsResponse.data;
        } catch (statsError) {
          console.warn('Failed to fetch stats for export:', statsError);
          exportData.statistics = null;
        }
      }

      // Export based on format
      if (exportOptions.format === 'json') {
        exportToJSON(exportData);
      } else if (exportOptions.format === 'csv' && exportData.readings) {
        exportToCSV(exportData.readings);
      }

      setSuccess(`Data exported successfully! ${exportData.readingsCount || 0} readings included.`);
    } catch (error: any) {
      console.error('Failed to export data:', error);
      setError(error.response?.data?.message || 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
          <p className="text-green-400 text-sm">{success}</p>
        </div>
      )}

      <Card variant="elevated">
        <h3 className="text-xl font-semibold text-white mb-4">Export Your Data</h3>
        <p className="text-gray-400 mb-6">
          Download a copy of your personal data including profile information, readings, and statistics.
        </p>

        <div className="space-y-6">
          {/* Data Selection */}
          <div>
            <h4 className="text-lg font-medium text-white mb-3">What to Include</h4>
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={exportOptions.includeProfile}
                  onChange={(e) => handleOptionChange('includeProfile', e.target.checked)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-white">Profile Information</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={exportOptions.includeReadings}
                  onChange={(e) => handleOptionChange('includeReadings', e.target.checked)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-white">Tarot Readings</span>
              </label>

              {exportOptions.includeReadings && (
                <label className="flex items-center gap-3 ml-6">
                  <input
                    type="checkbox"
                    checked={exportOptions.includePrivateReadings}
                    onChange={(e) => handleOptionChange('includePrivateReadings', e.target.checked)}
                    className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-gray-300">Include Private Readings</span>
                </label>
              )}

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={exportOptions.includeStats}
                  onChange={(e) => handleOptionChange('includeStats', e.target.checked)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-white">Statistics & Analytics</span>
              </label>
            </div>
          </div>

          {/* Date Range */}
          {exportOptions.includeReadings && (
            <div>
              <h4 className="text-lg font-medium text-white mb-3">Date Range</h4>
              <select
                value={exportOptions.dateRange}
                onChange={(e) => handleOptionChange('dateRange', e.target.value)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Time</option>
                <option value="last30">Last 30 Days</option>
                <option value="last90">Last 90 Days</option>
                <option value="lastyear">Last Year</option>
              </select>
            </div>
          )}

          {/* Format Selection */}
          <div>
            <h4 className="text-lg font-medium text-white mb-3">Export Format</h4>
            <div className="space-y-2">
              <label className="flex items-center gap-3">
                <input
                  type="radio"
                  name="format"
                  value="json"
                  checked={exportOptions.format === 'json'}
                  onChange={(e) => handleOptionChange('format', e.target.value)}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-white">JSON</span>
                  <p className="text-gray-400 text-sm">Complete data with full structure (recommended)</p>
                </div>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={exportOptions.format === 'csv'}
                  onChange={(e) => handleOptionChange('format', e.target.value)}
                  disabled={!exportOptions.includeReadings}
                  className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 focus:ring-primary-500 disabled:opacity-50"
                />
                <div>
                  <span className={`${!exportOptions.includeReadings ? 'text-gray-500' : 'text-white'}`}>
                    CSV (Readings Only)
                  </span>
                  <p className="text-gray-400 text-sm">Spreadsheet format for readings data</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">
                Your data will be downloaded as a file to your device.
              </p>
              <p className="text-gray-400 text-sm">
                No data is sent to external services during export.
              </p>
            </div>
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={isExporting || (!exportOptions.includeProfile && !exportOptions.includeReadings && !exportOptions.includeStats)}
            >
              {isExporting ? <Loading size="sm" /> : 'Export Data'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}